// ============================================================================
// Acceso a datos del panel. UNA sola puerta: si una pantalla necesita algo de
// la base, se agrega aquí (nunca consultas sueltas dentro de un componente).
//
// Reglas que NO se negocian:
//  - Lectura por tablas con RLS (lo que el usuario ve lo decide la base).
//  - ESCRITURA SIEMPRE POR RPC. `conversaciones` y `mensajes` son de solo
//    lectura para el usuario: enviar, tomar el chat, mover de etapa y marcar
//    leído pasan por funciones que validan membresía (patrón del ERP).
// ============================================================================

import type {
  AcuseDeEntrega, AcuseDeMedia,
  Alerta, Campania, ConfigLlamadas, SeccionGuion, Contacto, ContactoEmbudo, Conversacion, DatoFicha, EstadoCatalogo, Etapa, FichaCalificacion, FichaConocimiento, GuionActivo, Mensaje, Nota, PerfilBot, Plantilla,
  RespuestaRapida, Segmento, Seguimiento, VersionGuion, FilaCriterios, VersionCriterios, VozDelMes, VozResultados,
} from "./tipos.ts";
// ⛔⛔ EL TIPO DE LAS REGLAS DE PRECIO SALE DEL MOTOR, no se redeclara aquí
// (T-PRE-12). Si la forma que aplica `cotizar` y la que guarda el panel fueran
// dos declaraciones distintas, el día que una cambie la otra seguiría
// compilando — y el bot cotizaría con una config que la pantalla nunca supo
// enseñar.
import type { ReglasPrecio } from "../../../worker/src/tools/cotizar.ts";

type SB = any;

/** Bandeja: conversaciones del tenant, más reciente primero, con su contacto y
 *  el último mensaje. Lee de `v_inbox` (una consulta, no N+1). */
export async function listarConversaciones(sb: SB, tenantId: string): Promise<Conversacion[]> {
  const { data, error } = await sb
    .from("v_inbox").select("*")
    .eq("tenant_id", tenantId)
    .order("ultima_actividad", { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data ?? []).map(filaAConversacion);
}

/** Una fila de `v_inbox` → `Conversacion`.
 *  ⛔ UNA sola conversión, usada por la LISTA y por `leerConversacion` (T-LAT-3):
 *  con dos copias del mapeo, una columna nueva (como `nombre_equipo` y `titulo`
 *  de T-BAS-0) llegaría a una pantalla y a la otra no — sin error ninguno. */
export function filaAConversacion(f: any): Conversacion {
  return {
    id: f.id, tenant_id: f.tenant_id, contacto_id: f.contacto_id, canal_id: f.canal_id,
    estado: f.estado, asignada_a: f.asignada_a, ventana_expira_en: f.ventana_expira_en,
    ultima_actividad: f.ultima_actividad, no_leidos: f.no_leidos,
    ultimo_texto: f.ultimo_texto,
    contacto: {
      id: f.contacto_id, tenant_id: f.tenant_id, wa_id: f.wa_id,
      telefono_e164: f.telefono_e164, nombre: f.nombre, etapa_id: f.etapa_id,
      etiquetas: f.etiquetas ?? [], opt_in_estado: f.opt_in_estado,
      creado_en: f.contacto_creado_en,
      // T-BAS-0: las dos columnas que la 0055 añadió a `v_inbox`. Sin esto el
      // inbox seguiría enseñando el nombre de WhatsApp aunque el equipo ya lo
      // hubiera corregido — y sin error ninguno.
      nombre_equipo: f.nombre_equipo ?? null,
      titulo: f.titulo ?? null,
    },
  };
}

/** UNA conversación por id, con su contacto (T-LAT-3 · R-LAT-02).
 *  La necesita el panel lateral del embudo: la tarjeta trae `conversacion_id`
 *  pero no la conversación entera (estado, dueño, ventana de 24 h), y sin eso
 *  el `Hilo` no sabe si se puede escribir ni qué botón dibujar.
 *  ⚠️ `maybeSingle`: si la RLS o un id viejo no devuelven fila, es `null`, no
 *  un error — el panel enseña «sin conversación abierta», no una pantalla rota. */
export async function leerConversacion(sb: SB, convId: string): Promise<Conversacion | null> {
  const { data, error } = await sb.from("v_inbox").select("*").eq("id", convId).maybeSingle();
  if (error) throw error;
  return data ? filaAConversacion(data) : null;
}

/** Hilo completo de una conversación (append-only: siempre en orden de id). */
export async function listarMensajes(sb: SB, conversacionId: string): Promise<Mensaje[]> {
  const { data, error } = await sb
    .from("mensajes")
    .select(
      "id, conversacion_id, direccion, tipo, cuerpo, autor, autor_id, creado_en, " +
      "mensajes_entrega(estado), " +
      // C2-D4: la nota de voz o la foto del cliente. PostgREST la anida sola por
      // la FK a `mensajes(id)`; sin esto la burbuja del audio sale VACÍA.
      "mensajes_media(estado, storage_path, mime_type, transcripcion, confianza, error)",
    )
    .eq("conversacion_id", conversacionId)
    .order("id", { ascending: true })
    .limit(500);
  if (error) throw error;
  return (data ?? []).map((m: any) => ({
    ...m,
    entrega: m.mensajes_entrega?.[0]?.estado ?? m.mensajes_entrega?.estado ?? null,
    // Igual que `mensajes_entrega`: PostgREST devuelve lista o objeto según la
    // cardinalidad que infiera, y la diferencia rompe en runtime, no al compilar.
    media: m.mensajes_media?.[0] ?? m.mensajes_media ?? null,
  })) as Mensaje[];
}

/**
 * URL temporal para oír/ver un archivo del cliente (C2-D4).
 *
 * El bucket es PRIVADO a propósito —son fotos de su casa y su voz— así que no
 * hay URL fija: se pide una firmada que caduca. Quién puede pedirla lo decide la
 * política de `storage.objects` de la migración 0015: solo un miembro del
 * negocio dueño de la carpeta.
 *
 * Devuelve null en vez de lanzar: que no se pueda firmar un archivo no puede
 * tumbar el hilo entero del inbox.
 */
export async function urlDeMedia(
  sb: SB, storagePath: string, segundos = 600,
): Promise<string | null> {
  const { data, error } = await (sb as any).storage
    .from("chatai-media").createSignedUrl(storagePath, segundos);
  if (error || !data?.signedUrl) return null;
  return data.signedUrl as string;
}

export async function listarEtapas(sb: SB, tenantId: string): Promise<Etapa[]> {
  const { data, error } = await sb.from("etapas").select("*").eq("tenant_id", tenantId).order("orden");
  if (error) throw error;
  return (data ?? []) as Etapa[];
}

// ----------------------------------------------------------------- T-BAS-0
// La base del embudo (migración 0055). ⚠️ NINGUNA pantalla usa esto todavía:
// son los cimientos que consumirán T-EMB-1 y los tramos de la tarjeta.

/** El embudo: una fila por CONTACTO, con lo que dicen sus conversaciones
 *  ABIERTAS (`v_embudo`, `security_invoker`). Una consulta, no N+1.
 *  ⚠️ `nullsFirst: false`: un contacto sin conversación abierta tiene
 *  `ultima_actividad` nula y va al FINAL, no arriba del todo. */
export async function listarEmbudo(sb: SB, tenantId: string): Promise<ContactoEmbudo[]> {
  const { data, error } = await sb
    .from("v_embudo").select("*")
    .eq("tenant_id", tenantId)
    .order("ultima_actividad", { ascending: false, nullsFirst: false })
    .limit(500);
  if (error) throw error;
  return (data ?? []) as ContactoEmbudo[];
}

/** Edita la ficha del contacto por RPC (nunca `update` directo: I-C7-3).
 *  ⛔⛔ LOS TRES CAMPOS VAN SIEMPRE, y `null` significa VACIAR. No es un `patch`:
 *  omitir uno lo borra. Quien quiera tocar solo el título tiene que leer y
 *  reenviar los tres — la firma es así a propósito, para que no convivan dos
 *  convenciones. */
export async function editarContacto(
  sb: SB, contactoId: string,
  campos: { nombre_equipo: string | null; titulo: string | null; etiquetas: string[] },
) {
  const { error } = await sb.rpc("editar_contacto", {
    p_contacto: contactoId,
    p_nombre_equipo: campos.nombre_equipo,
    p_titulo: campos.titulo,
    p_etiquetas: campos.etiquetas,
  });
  if (error) throw error;
}

/** Renumera TODAS las etapas del negocio de una sola vez (solo el dueño).
 *  ⚠️ `ids` tiene que ser el conjunto COMPLETO de etapas del negocio en su orden
 *  nuevo: la base rechaza una lista parcial (P0025). Media renumeración dejaría
 *  columnas con el orden repetido. */
export async function reordenarEtapas(sb: SB, ids: string[]) {
  const { error } = await sb.rpc("reordenar_etapas", { p_ids: ids });
  if (error) throw error;
}

// ⚠️ Las TRES de abajo van por `insert`/`update`/`delete` DIRECTO y no por RPC, y
// es deliberado: la RLS del dueño sobre `etapas` (`etapas_wr` con
// `acceso_dueno`, migración 0005:146-148) ya existe desde C4 y es quien decide.
// Una RPC nueva aquí sería una segunda guarda escrita a mano encima de una que
// la base ya aplica sola. Lo que SÍ hace falta por RPC es reordenar, porque eso
// no es un permiso: es una renumeración que tiene que ser atómica.
// ⛔ Y BORRAR UNA ETAPA CON CONTACTOS DENTRO LOS DEJA «SIN ETAPA»: la FK de
// `contactos.etapa_id` es `on delete set null` (0005:32-34), así que el borrado
// no falla — se lleva los contactos a la nada visual. La pantalla de T-EMB-1
// tiene que IMPEDIRLO antes de llamar aquí (contar los contactos de la etapa y
// negarse), porque la base no lo va a impedir.

/** Crea una etapa al final del embudo. `orden` se calcula en el cliente a partir
 *  de `listarEtapas`: es el máximo + 1. ⚠️ Dos dueños creando a la vez chocarían
 *  con `unique (tenant_id, orden)` y el segundo vería el error — aceptable: son
 *  dos personas del mismo negocio pulsando «+ etapa» en el mismo segundo. */
export async function crearEtapa(
  sb: SB, tenantId: string,
  etapa: { nombre: string; color?: string; es_final?: boolean },
): Promise<Etapa> {
  const previas = await listarEtapas(sb, tenantId);
  const orden = previas.reduce((m, e) => Math.max(m, e.orden), 0) + 1;
  const { data, error } = await sb
    .from("etapas")
    .insert({
      tenant_id: tenantId, orden, nombre: etapa.nombre,
      ...(etapa.color === undefined ? {} : { color: etapa.color }),
      ...(etapa.es_final === undefined ? {} : { es_final: etapa.es_final }),
    })
    .select().single();
  if (error) throw error;
  return data as Etapa;
}

/** Renombra, recolorea o marca/desmarca como final. ⛔ NO toca `orden`: para eso
 *  está `reordenarEtapas`, que renumera todas a la vez sin chocar con el único. */
export async function actualizarEtapa(
  sb: SB, etapaId: string,
  campos: { nombre?: string; color?: string; es_final?: boolean },
) {
  const { error } = await sb.from("etapas").update(campos).eq("id", etapaId);
  if (error) throw error;
}

/** Borra una etapa. ⚠️ Ver el bloque de arriba: si tiene contactos, los deja
 *  «Sin etapa» y NO da error. Comprobarlo ANTES es de la pantalla. */
export async function borrarEtapa(sb: SB, etapaId: string) {
  const { error } = await sb.from("etapas").delete().eq("id", etapaId);
  if (error) throw error;
}

export async function listarContactos(sb: SB, tenantId: string): Promise<Contacto[]> {
  const { data, error } = await sb
    .from("contactos").select("*").eq("tenant_id", tenantId)
    .order("creado_en", { ascending: false }).limit(500);
  if (error) throw error;
  return (data ?? []) as Contacto[];
}

export async function listarNotas(sb: SB, contactoId: string): Promise<Nota[]> {
  const { data, error } = await sb
    .from("notas").select("id, contacto_id, autor_id, texto, creado_en")
    .eq("contacto_id", contactoId).order("creado_en", { ascending: false });
  if (error) throw error;
  return (data ?? []) as Nota[];
}

/** T-CAL-2 (R-FIC-01/02): lo que el bot ya anotó del cliente y si el negocio
 *  tiene la calificación encendida. RPC de SOLO LECTURA (`chatai.ficha_de_contacto`,
 *  invoker sobre `chatai_app.ficha_de_contacto`, que exige `acceso(tenant)` con JWT).
 *  ⚠️ Lleva el TENANT: la función real es `(p_tenant, p_contacto)` (0068:246).
 *  Lanza si la base rechaza; quien la llama decide que eso NO rompa la ficha (I-94). */
export async function leerFicha(sb: SB, tenantId: string, contactoId: string): Promise<FichaCalificacion> {
  const { data, error } = await sb.rpc("ficha_de_contacto", { p_tenant: tenantId, p_contacto: contactoId });
  if (error) throw error;
  return {
    habilitada: data?.habilitada === true,
    datos: Array.isArray(data?.datos) ? (data.datos as DatoFicha[]) : [],
  };
}

export async function listarRespuestasRapidas(sb: SB, tenantId: string): Promise<RespuestaRapida[]> {
  const { data, error } = await sb
    .from("respuestas_rapidas").select("id, atajo, texto")
    .eq("tenant_id", tenantId).eq("activo", true).order("atajo");
  if (error) throw error;
  return (data ?? []) as RespuestaRapida[];
}

// ---------------------------------------------------------------- escrituras

/** Envía un mensaje del asesor. La base rechaza texto libre fuera de la ventana
 *  de 24 h con P0003 — el error se propaga tal cual para que la UI lo explique. */
export async function enviarTexto(sb: SB, convId: string, texto: string, autorId: string | null) {
  // ⚠️ `responder_asesor` y NO `enviar_a_conversacion`: la segunda solo GUARDA
  // la fila. Durante unas horas del 1-ago el asesor escribía desde el panel, lo
  // veía en el inbox con su reloj de "pendiente"… y el cliente no recibía nada.
  // Esta RPC guarda Y encola la entrega real (C2-D6).
  const { data, error } = await sb.rpc("responder_asesor", {
    p_conv: convId, p_tipo: "texto", p_cuerpo: { texto }, p_autor_id: autorId,
  });
  if (error) throw error;
  return data as number;
}

/** Lo que WhatsApp acepta subir desde el inbox, para el `accept` del selector. */
export const FORMATOS_ADJUNTO =
  "image/jpeg,image/png,image/webp,image/gif," +
  "audio/ogg,audio/mpeg,audio/mp4,audio/aac,audio/wav,audio/webm," +
  "video/mp4," +
  "application/pdf,application/msword," +
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document," +
  "application/vnd.ms-excel," +
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet," +
  "application/vnd.ms-powerpoint," +
  "application/vnd.openxmlformats-officedocument.presentationml.presentation," +
  "text/plain,text/csv,application/zip";

/** Tope del bucket. Se avisa ANTES de subir para no hacer esperar en vano. */
export const TOPE_ADJUNTO_MB = 50;

/** A qué tipo de mensaje corresponde un archivo (el mismo criterio del worker). */
export function tipoDeAdjunto(mime: string): "imagen" | "audio" | "video" | "documento" {
  const m = (mime || "").split(";")[0].trim().toLowerCase();
  if (["image/jpeg", "image/png"].includes(m)) return "imagen";
  if (["audio/aac", "audio/amr", "audio/mpeg", "audio/mp4", "audio/ogg"].includes(m)) return "audio";
  if (["video/mp4", "video/3gp"].includes(m)) return "video";
  // Todo lo demás (Word, Excel, PDF, gif, wav…) va como documento: WhatsApp lo
  // entrega igual, solo que como archivo adjunto en vez de reproducirlo.
  return "documento";
}

/**
 * Adjunta un archivo a la conversación y lo manda al cliente (C2-D6).
 *
 * Dos pasos que NO se pueden invertir: primero el archivo al bucket, después el
 * mensaje. Al revés, un fallo de subida dejaría en el hilo un mensaje que apunta
 * a un archivo que no existe — y eso el cliente lo ve como un adjunto roto.
 *
 * La ruta lleva el tenant primero porque de eso depende el permiso (ver 0015).
 */
export async function adjuntarYEnviar(
  sb: SB,
  p: {
    convId: string; tenantId: string; archivo: File;
    pie?: string; autorId: string | null;
  },
): Promise<number> {
  const mb = p.archivo.size / (1024 * 1024);
  if (mb > TOPE_ADJUNTO_MB) {
    throw new Error(`El archivo pesa ${mb.toFixed(1)} MB y el máximo es ${TOPE_ADJUNTO_MB} MB.`);
  }
  const mime = p.archivo.type || "application/octet-stream";
  // Nombre saneado para la RUTA; el nombre ORIGINAL viaja aparte porque es el
  // que ve el cliente al recibir el documento.
  const limpio = (p.archivo.name || "archivo").replace(/[^A-Za-z0-9._-]/g, "_").slice(-60);
  const ruta = `${p.tenantId}/${p.convId}/salientes/${Date.now()}-${limpio}`;

  const { error: errSubida } = await (sb as any).storage
    .from("chatai-media").upload(ruta, p.archivo, { contentType: mime, upsert: false });
  if (errSubida) throw errSubida;

  const { data, error } = await sb.rpc("responder_asesor", {
    p_conv: p.convId,
    p_tipo: tipoDeAdjunto(mime),
    p_cuerpo: {
      storage_path: ruta,
      nombre_archivo: p.archivo.name || "archivo",
      mime_type: mime,
      ...(p.pie ? { pie: p.pie } : {}),
    },
    p_autor_id: p.autorId,
  });
  if (error) throw error;
  return data as number;
}

/**
 * El BOT la tenía: se transfiere Y se marca dueño. Son dos verbos y por eso son
 * dos RPC — `transferir_a_humano` NO toca `asignada_a`.
 *
 * ⚠️ EL ORDEN ES LOAD-BEARING: transferir → asignar. Si la asignación falla, la
 * conversación queda «esperando asesor», que es el estado seguro (cualquiera la
 * ve libre y la toma). Al revés quedaría con dueño mientras el bot le sigue
 * hablando al cliente, que es justo lo que tomar el chat existe para evitar.
 * ⚠️ Las dos RPC NO son atómicas y no pueden serlo sin migración: el orden es la
 * mitigación, no la solución.
 */
export async function tomarChat(
  sb: SB, convId: string, usuarioId: string, motivo = "Un asesor tomó la conversación",
) {
  await transferirAHumano(sb, convId, motivo);
  await asignarConversacion(sb, convId, usuarioId);
}

/**
 * Solo el PASE: el bot suelta la conversación y se la deja a una persona.
 *
 * 🔴 Está separada de `tomarChat` porque su fallo NO se puede tratar igual que
 * el de la asignación. Si esto falla, la conversación **se queda en `bot`** y el
 * worker sigue creyendo que manda él: le contesta al cliente por encima del
 * asesor y el cliente ve dos voces contradiciéndose. Ese error tiene que subir
 * y verse; el de la asignación, no (peor sería tragarse el mensaje del asesor).
 */
export async function transferirAHumano(
  sb: SB, convId: string, motivo = "Un asesor tomó la conversación",
) {
  const { error } = await sb.rpc("transferir_a_humano", { p_conv: convId, p_motivo: motivo });
  if (error) throw error;
}

/**
 * Ya estaba en `humano` SIN dueño (la escaló el bot): SOLO se asigna.
 *
 * ⛔ NO llama a `transferir_a_humano`. La conversación ya está en `humano`, y
 * llamarlo otra vez insertaría una SEGUNDA fila `transferido_humano` con el
 * motivo del panel — pisando el literal que es el observable de la compuerta
 * del tramo del pase al humano (se comprueba contando ESE motivo, y tiene que
 * dar cero).
 */
export async function reclamarChat(sb: SB, convId: string, usuarioId: string) {
  await asignarConversacion(sb, convId, usuarioId);
}

/**
 * ⭐ T-HIB-1 · R-HIB-01 · una INSTRUCCIÓN al bot («Ofrécele el Eco con envío
 * gratis y sigue tú»). La base la guarda como nota INTERNA —nunca sale a
 * WhatsApp—, le devuelve el chat al bot y encola su turno: el bot le escribe al
 * cliente enseguida, con sus palabras (migr 0066).
 * ⛔ `instruir_al_bot` y NUNCA `responder_asesor`: la segunda le MANDA el texto
 * al cliente tal cual.
 */
export async function instruirAlBot(sb: SB, convId: string, texto: string) {
  const { data, error } = await sb.rpc("instruir_al_bot", { p_conv: convId, p_texto: texto });
  if (error) throw error;
  return data as number;
}

export async function devolverAlBot(sb: SB, convId: string) {
  const { error } = await sb.rpc("devolver_a_bot", { p_conv: convId });
  if (error) throw error;
}

export async function cerrarConversacion(sb: SB, convId: string) {
  const { error } = await sb.rpc("cerrar_conversacion", { p_conv: convId });
  if (error) throw error;
}

export async function marcarLeida(sb: SB, convId: string) {
  const { error } = await sb.rpc("marcar_leida", { p_conv: convId });
  if (error) throw error;
}

export async function asignarConversacion(sb: SB, convId: string, usuarioId: string | null) {
  const { error } = await sb.rpc("asignar_conversacion", { p_conv: convId, p_usuario: usuarioId });
  if (error) throw error;
}

export async function moverEtapa(sb: SB, contactoId: string, etapaId: string | null) {
  const { error } = await sb.rpc("mover_etapa", { p_contacto: contactoId, p_etapa: etapaId });
  if (error) throw error;
}

// ── T-CAL-19 · EL INTERRUPTOR DE MOVER Y EL CANDADO (migr 0074) ───────────────
/** ¿El bot mueve las tarjetas en este negocio? RPC de SOLO LECTURA
 *  (`chatai.mover_automatico`, exige `acceso(tenant)`).
 *  ⚠️ `null` = la calificación está APAGADA en el negocio (sin criterios activos o
 *  `calificacion_habilitada = false`): el embudo NO pinta ni el interruptor ni el
 *  candado. Lanza si la base rechaza; quien la llama decide que eso no rompa el tablero. */
export async function leerMoverAutomatico(sb: SB, tenantId: string): Promise<boolean | null> {
  const { data, error } = await sb.rpc("mover_automatico", { p_tenant: tenantId });
  if (error) throw error;
  return typeof data === "boolean" ? data : null;
}

/** Enciende o apaga «El bot mueve las tarjetas». SOLO el dueño (la base da 42501 a
 *  cualquier otro, I-129). Devuelve el valor que quedó puesto. */
export async function fijarMoverAutomatico(sb: SB, tenantId: string, valor: boolean): Promise<boolean> {
  const { data, error } = await sb.rpc("fijar_mover_automatico", { p_tenant: tenantId, p_valor: valor });
  if (error) throw error;
  return data === true;
}

/** «Soltar»: la tarjeta que movió una persona vuelve al automático SIN cambiar de
 *  etapa (I-130). Cualquier miembro del negocio. `true` si había candado. */
export async function soltarCandado(sb: SB, contactoId: string): Promise<boolean> {
  const { data, error } = await sb.rpc("soltar_candado", { p_contacto: contactoId });
  if (error) throw error;
  return data === true;
}

export async function crearNota(sb: SB, contactoId: string, texto: string) {
  const { data, error } = await sb.rpc("crear_nota", { p_contacto: contactoId, p_texto: texto });
  if (error) throw error;
  return data as string;
}

// ------------------------------------------------------------------ realtime

/** El cajón donde vive Chat AI. ⚠️ Realtime NO lo hereda del cliente: el
 *  `db: { schema: "chatai" }` de `createClient` solo manda en PostgREST, y cada
 *  suscripción tiene que decir su esquema aparte. Hasta la noche del 1-ago-2026 estas dos
 *  decían `public` —donde no hay ninguna tabla nuestra— y el resultado era un
 *  FALLO MUDO: nunca llegaba un evento, nadie recibía un error, y con un solo
 *  asesor ni se notaba (recargaba y veía todo). Con dos, se contestaban encima.
 *  Si mañana se agrega otra suscripción, va con esta constante. */
export const ESQUEMA = "chatai";

/** Suscripción del inbox (C2-D5): mensajes nuevos, cambios de conversación y
 *  —desde T-PAN, migración 0047— los DOS satélites mutables de un mensaje.
 *  Devuelve la función para desuscribirse. La RLS filtra por tenant en el
 *  servidor: aun así comparamos tenant_id porque Realtime no lo garantiza.
 *
 *  ⭐⭐ POR QUÉ SON CUATRO Y NO DOS. `mensajes` es APPEND-ONLY por trigger
 *  (0001:129-135): lo que a un mensaje le PASA después de nacer —salió, lo
 *  recibieron, lo leyeron, el archivo se cayó— no cabe en esa tabla por diseño y
 *  vive en `mensajes_entrega` y `mensajes_media`. Escuchar solo las dos tablas
 *  del NACIMIENTO es oír la mitad de la película: el panel pinta el mensaje al
 *  instante y su palomita se queda congelada hasta que alguien recarga (medido
 *  por Cristhian el 31-ago-2026, dicho tres veces).
 *
 *  ⛔ LOS CUATRO CALLBACKS SON OBLIGATORIOS, no opcionales. Un callback opcional
 *  que nadie pasa deja la suscripción viva, el socket entregando y la pantalla
 *  quieta: exactamente el mismo fallo mudo que la trampa del `ESQUEMA`, pero un
 *  piso más arriba y sin nada que lo delate. Aquí el compilador lo delata. */
/** En qué anda el canal en vivo, en dos palabras y no en la jerga de la
 *  librería. `caido` es TODO lo que no sea estar suscrito: error, tiempo
 *  agotado o cerrado — al panel le da igual cuál de los tres sea.
 *
 *  ⚠️⚠️ LO QUE ESTE ESTADO **NO** PUEDE DECIR, Y ESTÁ MEDIDO (1-sep-2026, sobre
 *  el panel publicado, con la red cortada de verdad): **durante los primeros
 *  ~40 s de un corte el canal SIGUE diciendo `conectado` y ya no entrega nada.**
 *  El socket no se entera hasta que le falla el latido. ⇒ Ninguna pieza que
 *  mire este estado puede prometer «avisa siempre que el panel esté sordo»:
 *  promete «avisa cuando el canal se declara caído». Escribirlo de otro modo
 *  sería una vara que miente. Medición: `sdd/MEDICION-T-PAN-5.md`. */
export type EstadoCanal = "conectado" | "caido";

export function suscribirInbox(
  sb: SB, tenantId: string,
  al: {
    mensaje: (m: Mensaje) => void;
    conversacion: (c: Partial<Conversacion>) => void;
    entrega: (a: AcuseDeEntrega) => void;
    media: (a: AcuseDeMedia) => void;
    /** ⛔ OBLIGATORIO como los otros cuatro, y por la MISMA razón (T-PAN-5).
     *  Un aviso opcional que nadie pasa deja el canal muerto, la pantalla
     *  quieta y a nadie enterado: el fallo mudo de siempre, un piso más
     *  arriba. Aquí lo delata el compilador. */
    estado: (e: EstadoCanal) => void;
  },
): () => void {
  const canal = sb
    .channel(`panel:${tenantId}`)
    .on("postgres_changes", { event: "INSERT", schema: ESQUEMA, table: "mensajes" },
      (p: any) => { if (p.new?.tenant_id === tenantId) al.mensaje(p.new as Mensaje); })
    .on("postgres_changes", { event: "*", schema: ESQUEMA, table: "conversaciones" },
      (p: any) => { if (p.new?.tenant_id === tenantId) al.conversacion(p.new as Partial<Conversacion>); })
    // ⚠️ `*` y no `UPDATE`: la fila de entrega NACE en `encolado` dentro de la
    // misma transacción que el mensaje (0003:170) y después se actualiza con
    // cada status de Meta. Con `UPDATE` a secas, el relojito del mensaje recién
    // enviado no aparecería hasta el primer acuse.
    .on("postgres_changes", { event: "*", schema: ESQUEMA, table: "mensajes_entrega" },
      (p: any) => { if (p.new?.tenant_id === tenantId) al.entrega(acuseDeEntrega(p.new)); })
    // ⚠️ También `*`: el trigger de la 0045 INSERTA la fila del adjunto del
    // asesor en `guardado`, y el worker la UPDATEa a `fallido` si la entrega
    // muere de verdad. El insert importa tanto como el update.
    .on("postgres_changes", { event: "*", schema: ESQUEMA, table: "mensajes_media" },
      (p: any) => { if (p.new?.tenant_id === tenantId) al.media(acuseDeMedia(p.new)); })
    // ⭐⭐ EL `.subscribe()` DEJA DE IR PELADO (T-PAN-5). Antes nadie miraba el
    // estado del canal: se caía, volvía, y el panel seguía pintando lo de hace
    // un rato sin que nada lo delatara. Medido el 1-sep: reconecta y se
    // resuscribe solo, pero **no hace una sola consulta al volver** — el hueco
    // no se repone, porque Realtime no repite lo que pasó mientras no estabas.
    // ⛔ La DECISIÓN de qué hacer no vive aquí: esta función conecta, no manda
    // en la pantalla. Solo entrega el estado a quien la llamó.
    .subscribe((s: string) => { al.estado(s === "SUBSCRIBED" ? "conectado" : "caido"); });
  return () => { try { sb.removeChannel(canal); } catch { /* ya desmontado */ } };
}

/**
 * ⚠️ EL `mensaje_id` SE FUERZA A NÚMERO, Y NO ES PARANOIA. Es un `bigint`, y el
 * hilo lo casa con `Mensaje.id` usando `===`. Si algún día Realtime lo entregara
 * como cadena —o si alguien mete un doble en un test— el `===` sería falso
 * SIEMPRE: cero errores, cero eventos aplicados, la palomita quieta y todo en
 * verde. Es el mismo veneno que el `schema: "public"` del 1-ago-2026.
 *
 * ⚠️ Y se copian los campos UNO A UNO en vez de esparcir la fila entera: la fila
 * de `mensajes_media` trae `tenant_id`, `media_id`, `intentos`, `proveedor`… que
 * el panel no usa. Esparcirla metería en el estado de React datos que nadie
 * declaró, y el día que uno de ellos se llame igual que un campo del mensaje
 * (`estado`, sin ir más lejos) lo pisaría en silencio.
 */
function acuseDeEntrega(fila: any): AcuseDeEntrega {
  return { mensaje_id: Number(fila.mensaje_id), estado: fila.estado };
}

function acuseDeMedia(fila: any): AcuseDeMedia {
  return {
    mensaje_id: Number(fila.mensaje_id),
    estado: fila.estado,
    storage_path: fila.storage_path ?? null,
    mime_type: fila.mime_type ?? null,
    transcripcion: fila.transcripcion ?? null,
    // `numeric` viaja como número, pero un `null` de la base y un `undefined` de
    // un payload recortado no son lo mismo para `typeof … === "number"` de
    // `Hilo.tsx:62`. Se normaliza aquí, una vez.
    confianza: fila.confianza === null || fila.confianza === undefined ? null : Number(fila.confianza),
    error: fila.error ?? null,
  };
}

/** ⭐ T-HIB-1 · el aviso cuando el chat lo tiene otra persona (la caja de la orden no sale). */
export const CHAT_DE_OTRA_PERSONA =
  "Este chat lo tiene otra persona: la instrucción al bot solo la puede dar quien lo tiene (o nadie).";

/** Traduce los errores de la base a algo que un asesor entienda. */
export function explicarError(e: any): string {
  const code = e?.code ?? "";
  // Ojo: `String({})` es "[object Object]". Un error sin mensaje se cae al
  // texto genérico antes que enseñarle basura a un asesor.
  const crudo = e?.message ?? (typeof e === "string" ? e : "");
  const msg = typeof crudo === "string" ? crudo : "";
  if (code === "P0003" || /VENTANA_CERRADA/.test(msg))
    return "Pasaron más de 24 horas desde el último mensaje del cliente: WhatsApp solo permite responder con una plantilla aprobada.";
  // ⚠️ La RLS de Storage va ANTES del `42501`: una violación de política REAL de
  // Postgres también trae ese código, y ahí «no tienes acceso a esta conversación»
  // manda al asesor a buscar el problema donde no está. Nuestras RPCs lanzan 42501
  // con el texto «sin acceso a la conversación», que nunca dice «row-level
  // security», así que este orden no le quita nada al `if` de abajo.
  // 🔴 Camino VIVO: un usuario staff de Victorix (claim `es_victorix`) SIN fila en
  // `miembros` pasa la RPC —`0016:42` acepta `es_miembro` **o** `soy_victorix()`—
  // pero NO la política de subida (`0016:110-117`, que solo mira `es_miembro`).
  // Storage devuelve 403 y hasta hoy se mostraba en crudo: «new row violates
  // row-level security policy». ⛔ La política no se toca: lo que falta es la
  // membresía.
  // ⚠️ La frase va SIN mencionar archivos: `explicarError` la usan 9 componentes
  // (inbox, embudo, contactos, métricas, equipo, seguimientos, campañas…) y la
  // misma política puede rechazar otra cosa. El diagnóstico —falta la membresía—
  // es correcto en todos; la palabra «archivos» no.
  if (/row-level security/i.test(msg))
    return "Tu cuenta puede ver este negocio pero no escribir en él: le falta la membresía. Pídele al dueño que te dé acceso.";
  // ⭐ T-HIB-1 (0066, fiscal H6): va ANTES del 42501 genérico, que también es su código.
  if (/CHAT_DE_OTRA_PERSONA/.test(msg) || e?.details === "VX_CHAT_DE_OTRA_PERSONA")
    return CHAT_DE_OTRA_PERSONA;
  // ⭐ T-CAL-19 (0074): también son 42501, y «esta conversación» mentiría aquí.
  if (/SOLO_EL_DUENO/.test(msg)) return "Solo el dueño del negocio puede cambiar si el bot mueve las tarjetas.";
  if (code === "P0033" || /SIN_CRITERIOS/.test(msg)) return "Este negocio todavía no tiene configurada la calificación.";
  if (code === "42501" || /sin acceso/.test(msg)) return "No tienes acceso a esta conversación.";
  if (/ETAPA_DE_OTRO_TENANT/.test(msg)) return "Esa etapa es de otro negocio.";
  // --- T-BAS-0 (migración 0055). Se decide por el CÓDIGO y, de refuerzo, por el
  // marcador del mensaje: el texto del `raise` se reescribe y estos `if`
  // dejarían de casar en silencio, pero el código no cambia.
  if (code === "P0020" || /TITULO_LARGO/.test(msg))
    return "El título no puede pasar de 60 letras.";
  if (code === "P0021" || /NOMBRE_LARGO/.test(msg))
    return "El nombre no puede pasar de 80 letras.";
  if (code === "P0022" || /DEMASIADAS_ETIQUETAS/.test(msg))
    return "Máximo 20 etiquetas por contacto.";
  if (code === "P0023" || /ETIQUETA_LARGA/.test(msg))
    return "Una etiqueta no puede pasar de 30 letras.";
  if (code === "P0024" || /LISTA_VACIA/.test(msg))
    return "No se envió ninguna etapa para reordenar.";
  if (code === "P0025" || /LISTA_INCOMPLETA/.test(msg))
    return "Falta alguna etapa en la lista: recarga la pantalla y vuelve a ordenarlas.";
  if (/ASIGNADO_NO_ES_MIEMBRO/.test(msg)) return "Esa persona no es del equipo.";
  if (/NOTA_VACIA/.test(msg)) return "La nota está vacía.";
  // --- C2-D6: la ruta del adjunto (migración 0036). Los CUATRO `raise` del
  // trigger comparten el errcode 45006 y el marcador `VX_RUTA_AJENA`.
  // ⚠️ Con la 0036 tal como quedó, ESTE es el único sitio donde el asesor se
  // entera: ya no hay rechazo previo de Storage (plan §3.2).
  //
  // ⛔ EL TEXTO NO PUEDE INVITAR A REINTENTAR. `adjuntarYEnviar` sube el archivo
  // al bucket ANTES de llamar la RPC, y `authenticated` no tiene UPDATE ni DELETE
  // sobre `storage.objects` (0016:118-123, a propósito): cada reintento deja un
  // objeto HUÉRFANO que nadie puede borrar — y la Ley 1581 exige poder suprimir.
  // Un «vuelve a intentarlo» aquí es una fábrica de basura no borrable.
  //
  // ⚠️ La condición (1) del trigger va aparte: ahí la conversación NO SE PUDO
  // RESOLVER, así que decirle «no corresponde a esta conversación» sería un
  // diagnóstico FALSO. Este `if` es el único que mira el TEXTO y no el marcador;
  // se permite porque si el texto se reescribe, cae al caso general de abajo —
  // nunca al error crudo. El marcador sigue siendo lo que decide.
  if (/VX_RUTA_AJENA/.test(msg) && /resolver el negocio/.test(msg))
    return "No se pudo enviar el archivo: no se pudo identificar la conversación. Recarga la pantalla y vuelve a abrir el chat.";
  if (/VX_RUTA_AJENA/.test(msg))
    return "No se pudo enviar el archivo: su ubicación no corresponde a esta conversación. Recarga la pantalla antes de volver a intentarlo — reintentar sin recargar deja copias que ya no se pueden borrar.";
  // --- C10: accesos. Se decide por el MARCADOR (VX_*), nunca por el texto del
  // mensaje: el texto se reescribe y estos `if` dejarían de casar en silencio.
  if (/VX_NO_DUENO/.test(msg)) return "Solo el dueño del negocio puede administrar el equipo.";
  if (/VX_ULTIMO_DUENO/.test(msg))
    return "No puedes quitarte a ti mismo: el negocio se quedaría sin ningún dueño. Nombra a otro primero.";
  if (/VX_YA_ES_MIEMBRO/.test(msg)) return "Esa persona ya tiene acceso a este negocio.";
  if (/VX_CORREO_INVALIDO/.test(msg)) return "Ese correo no parece válido. Revísalo.";
  if (/VX_NO_MIEMBRO/.test(msg)) return "Esa persona ya no está en este negocio.";
  if (/VX_YA_USADA/.test(msg)) return "Esa invitación ya se usó.";
  if (/VX_VENCIDA/.test(msg)) return "Esa invitación venció. Manda una nueva.";
  if (/VX_REVOCADA/.test(msg)) return "Esa invitación fue cancelada.";
  if (/VX_OTRO_CORREO/.test(msg)) return "Esa invitación es para otro correo.";
  return msg || "Algo salió mal.";
}

// ---------- C4-D2: seguimientos ---------------------------------------------

/** Bandeja: lo pendiente primero (lo vencido arriba), y luego el historial. */
export async function listarSeguimientos(
  sb: SB, tenantId: string, soloPendientes = true,
): Promise<Seguimiento[]> {
  let q = sb.from("v_seguimientos").select("*").eq("tenant_id", tenantId);
  if (soloPendientes) q = q.in("estado", ["pendiente", "manual"]);
  const { data, error } = await q.order("vence", { ascending: true }).limit(300);
  if (error) throw error;
  return (data ?? []) as Seguimiento[];
}

export async function agendarSeguimiento(
  sb: SB, contactoId: string, vence: Date, motivo: string,
  mensajeSugerido?: string | null, conversacionId?: string | null,
): Promise<string> {
  const { data, error } = await sb.rpc("agendar_seguimiento", {
    p_contacto: contactoId, p_vence: vence.toISOString(), p_motivo: motivo,
    p_mensaje_sugerido: mensajeSugerido ?? null, p_creado_por: "humano",
    p_conversacion: conversacionId ?? null,
  });
  if (error) throw error;
  return data as string;
}

/** Único camino para cerrar un seguimiento: la RPC pone la fecha (I-C4-1). */
export async function resolverSeguimiento(
  sb: SB, id: string, estado: "hecho" | "cancelado", detalle: Record<string, unknown> = {},
): Promise<void> {
  const { error } = await sb.rpc("resolver_seguimiento", {
    p_id: id, p_estado: estado, p_detalle: detalle,
  });
  if (error) throw error;
}

// ---------- C4: contactos y métricas ----------------------------------------

export interface ResumenImport {
  creados: number;
  actualizados: number;
  ignorados: number;
  detalle: Array<{ wa_id: string; motivo: string }>;
}

/** Alta masiva. Los contactos entran SIN opt-in: importar no es consentir. */
export async function importarContactos(
  sb: SB, tenantId: string,
  filas: Array<{ wa_id: string; telefono: string | null; nombre: string | null; etiquetas: string[] }>,
): Promise<ResumenImport> {
  const { data, error } = await sb.rpc("importar_contactos", {
    p_tenant: tenantId, p_filas: filas,
  });
  if (error) throw error;
  const f = Array.isArray(data) ? data[0] : data;
  return {
    creados: f?.creados ?? 0, actualizados: f?.actualizados ?? 0,
    ignorados: f?.ignorados ?? 0, detalle: f?.detalle ?? [],
  };
}

export interface Metricas {
  desde: string;
  conversaciones: number;
  conversaciones_nuevas: number;
  contactos_nuevos: number;
  mensajes_recibidos: number;
  mensajes_enviados: number;
  atendidas_solo_bot: number;
  pasaron_a_humano: number;
  seguimientos_cumplidos: number;
  seguimientos_pendientes: number;
  costo_ia_usd: number;
}

export async function metricasResumen(sb: SB, tenantId: string, dias = 30): Promise<Metricas> {
  const { data, error } = await sb.rpc("metricas_resumen", { p_tenant: tenantId, p_dias: dias });
  if (error) throw error;
  const f = Array.isArray(data) ? data[0] : data;
  // Postgres devuelve bigint/numeric como TEXTO en JSON (no cabe en un número
  // de JS sin perder precisión). Sin este Number(), "24" > "3" sería false.
  const n = (v: unknown) => Number(v ?? 0);
  return {
    desde: f?.desde ?? "",
    conversaciones: n(f?.conversaciones),
    conversaciones_nuevas: n(f?.conversaciones_nuevas),
    contactos_nuevos: n(f?.contactos_nuevos),
    mensajes_recibidos: n(f?.mensajes_recibidos),
    mensajes_enviados: n(f?.mensajes_enviados),
    atendidas_solo_bot: n(f?.atendidas_solo_bot),
    pasaron_a_humano: n(f?.pasaron_a_humano),
    seguimientos_cumplidos: n(f?.seguimientos_cumplidos),
    seguimientos_pendientes: n(f?.seguimientos_pendientes),
    costo_ia_usd: n(f?.costo_ia_usd),
  };
}

// ── T-CAL-9 · EL CUADRO DE NÚMEROS DEL EMBUDO CALIFICADO (migr 0077) ──────────
/** Las cifras del embudo calificado en N días (`chatai.cuadro_embudo`, misma
 *  puerta que `metricas_resumen`: miembro o staff; otro negocio ⇒ 42501, I-142).
 *  ⚠️ Los minutos llegan como TEXTO («7.0»): numeric de Postgres. `null` = ningún
 *  🔥 tomado en el periodo. `contando_desde` = el primer evento registrado (null si
 *  aún no hay ninguno): calificados, 🔥, pasados y tomados no existían antes. */
export interface CuadroEmbudo {
  desde: string;
  entraron: number;
  calificados: number;
  calientes: number;
  pasados: number;
  tomados: number;
  vendidos: number;
  minutos_mediana_tomar: number | null;
  minutos_max_tomar: number | null;
  pasados_sin_tomar: number;
  contando_desde: string | null;
}

export async function cuadroEmbudo(sb: SB, tenantId: string, dias = 7): Promise<CuadroEmbudo> {
  const { data, error } = await sb.rpc("cuadro_embudo", { p_tenant: tenantId, p_dias: dias });
  if (error) throw error;
  const f = Array.isArray(data) ? data[0] : data;
  const n = (v: unknown) => Number(v ?? 0);
  const talVez = (v: unknown) => (v === null || v === undefined ? null : Number(v));
  return {
    desde: f?.desde ?? "",
    entraron: n(f?.entraron),
    calificados: n(f?.calificados),
    calientes: n(f?.calientes),
    pasados: n(f?.pasados),
    tomados: n(f?.tomados),
    vendidos: n(f?.vendidos),
    minutos_mediana_tomar: talVez(f?.minutos_mediana_tomar),
    minutos_max_tomar: talVez(f?.minutos_max_tomar),
    pasados_sin_tomar: n(f?.pasados_sin_tomar),
    contando_desde: f?.contando_desde ?? null,
  };
}

export interface EtapaConteo {
  etapa_id: string; nombre: string; orden: number; color: string; contactos: number;
}

export async function embudoConteo(sb: SB, tenantId: string): Promise<EtapaConteo[]> {
  const { data, error } = await sb
    .from("v_embudo_conteo").select("*").eq("tenant_id", tenantId).order("orden");
  if (error) throw error;
  return (data ?? []).map((e: any) => ({ ...e, contactos: Number(e.contactos ?? 0) }));
}

// ---------- C5: campañas, plantillas, segmentos, bajas ----------------------

export async function listarPlantillas(sb: SB, tenantId: string): Promise<Plantilla[]> {
  const { data, error } = await sb.from("plantillas")
    .select("id, nombre_meta, categoria, idioma, estado_meta, motivo_rechazo")
    .eq("tenant_id", tenantId).order("nombre_meta");
  if (error) throw error;
  return (data ?? []) as Plantilla[];
}

export async function listarSegmentos(sb: SB, tenantId: string): Promise<Segmento[]> {
  const { data, error } = await sb.from("segmentos").select("id, nombre, filtro")
    .eq("tenant_id", tenantId).order("nombre");
  if (error) throw error;
  return (data ?? []) as Segmento[];
}

export async function listarCampanias(sb: SB, tenantId: string): Promise<Campania[]> {
  const { data, error } = await sb.from("campanias").select("*")
    .eq("tenant_id", tenantId).order("creado_en", { ascending: false }).limit(100);
  if (error) throw error;
  return (data ?? []) as Campania[];
}

/** Cuántos contactos recibirían de verdad la campaña, YA sin los que no dieron
 *  permiso: es el número que se le enseña antes de confirmar. */
export async function contarDestinatarios(sb: SB, segmentoId: string): Promise<number> {
  const { data, error } = await sb.rpc("contactos_de_segmento", { p_segmento: segmentoId });
  if (error) throw error;
  return (data ?? []).length;
}

export async function crearCampania(
  sb: SB, tenantId: string, nombre: string, plantillaId: string, segmentoId: string,
): Promise<string> {
  const { data, error } = await sb.from("campanias")
    .insert({ tenant_id: tenantId, nombre, plantilla_id: plantillaId, segmento_id: segmentoId })
    .select("id").single();
  if (error) throw error;
  return data.id as string;
}

/** El acto humano que exige I-C5-2. Solo el dueño puede. */
export async function aprobarCampania(sb: SB, campaniaId: string): Promise<void> {
  const { error } = await sb.rpc("aprobar_campania", { p_campania: campaniaId });
  if (error) throw error;
}

export async function registrarOptOut(sb: SB, contactoId: string, texto?: string): Promise<void> {
  const { error } = await sb.rpc("registrar_opt_out", {
    p_contacto: contactoId, p_origen: "manual", p_texto: texto ?? null,
  });
  if (error) throw error;
}

/**
 * El RESULTADO de una campaña, no su estado.
 *
 * `salieron` cuenta lo que Meta aceptó; los demás son lo que Meta (o el cliente)
 * respondió después. ⚠️ Son ACUMULATIVOS hacia arriba, no excluyentes: quien
 * contestó también llegó y también leyó. La pantalla los muestra así porque
 * «12 llegaron de 14» se entiende, y «3 en estado leído» no.
 */
export interface ResultadoCampania {
  total: number; salieron: number; llegaron: number;
  leyeron: number; contestaron: number; rebotaron: number;
}

/**
 * Los resultados de VARIAS campañas en UNA consulta. Antes esto era una función
 * por campaña y no la usaba ninguna pantalla: la base sabía si el mensaje había
 * llegado y nadie podía verlo.
 */
export async function resultadosDeCampanias(
  sb: SB, campaniaIds: string[],
): Promise<Map<string, ResultadoCampania>> {
  const salida = new Map<string, ResultadoCampania>();
  if (campaniaIds.length === 0) return salida;

  // ⚠️ SE CUENTA EN LA BASE, no trayendo las filas (migr 0034). La versión
  // anterior pedía los envíos y contaba aquí: PostgREST corta en `max_rows`
  // (1000) **sin dar error y con 200**, y sin `order by` ni siquiera es
  // determinista cuáles llegan. El recordatorio de SIA son 14 × 52 semanas =
  // 728 filas al año en UNA campaña: al pasar el tope, «12 de 14 salieron» se
  // volvía un número arbitrario que cambiaba entre dos recargas. Un número
  // tranquilizador que no da error es peor que un error.
  const { data, error } = await sb.rpc("resultados_de_campanias", { p_campanias: campaniaIds });
  if (error) throw error;

  for (const f of (data ?? []) as any[]) {
    salida.set(f.campania_id, {
      // Postgres devuelve `bigint` como TEXTO en JSON: sin `Number()`,
      // «12» > «3» es false y las comparaciones salen mal sin fallar.
      total: Number(f.total), salieron: Number(f.salieron),
      llegaron: Number(f.llegaron), leyeron: Number(f.leyeron),
      contestaron: Number(f.contestaron), rebotaron: Number(f.rebotaron),
    });
  }
  return salida;
}

// ---------- Perfil del bot (C3-D8 / C2-D7) ----------------------------------
// ⚠️ Estas dos escriben DIRECTO a la tabla, contra la regla del encabezado, y
// es a propósito: `perfil_bot` se diseñó para que la editara el dueño desde el
// panel (grants + policy `acceso_dueno` en la migración 0004). No hay lógica
// que proteger detrás de una RPC — quien no sea dueño simplemente no cambia
// ninguna fila, lo decide la base.

export async function leerPerfilBot(sb: SB, tenantId: string): Promise<PerfilBot | null> {
  const { data, error } = await sb
    .from("perfil_bot").select("*").eq("tenant_id", tenantId).maybeSingle();
  if (error) throw error;
  return (data as PerfilBot) ?? null;
}

export async function guardarPerfilBot(
  sb: SB, tenantId: string, cambios: Partial<PerfilBot>,
): Promise<PerfilBot> {
  // `upsert` y no `update`: un negocio dado de alta después de la migración
  // 0017 puede no tener fila todavía, y ahí un `update` no falla — no hace
  // NADA, que es peor: el dueño ve "guardado" y al recargar está como antes.
  const { data, error } = await sb
    .from("perfil_bot")
    .upsert({ tenant_id: tenantId, ...cambios }, { onConflict: "tenant_id" })
    .select().single();
  if (error) throw error;
  return data as PerfilBot;
}

// ---------- T-ENT-GUION-1 (migr 0088): secciones del guion y config de llamadas ------
// ⚠️ Directo a la tabla: la RLS (solo Victorix escribe, `acceso` lee) es la defensa.
// Se pinta LO QUE QUEDÓ en la base (`.select()`), no lo que se envió.

export async function leerSeccionesGuion(sb: SB, tenantId: string): Promise<SeccionGuion[]> {
  const { data, error } = await sb.from("guion_secciones").select("*")
    .eq("tenant_id", tenantId).order("orden", { ascending: true }).order("actualizado_en", { ascending: true });
  if (error) throw error;
  return (data ?? []) as SeccionGuion[];
}

export async function crearSeccionGuion(
  sb: SB, tenantId: string, s: Pick<SeccionGuion, "titulo" | "contenido" | "canal" | "orden">,
): Promise<SeccionGuion> {
  const { data, error } = await sb.from("guion_secciones")
    .insert({ tenant_id: tenantId, ...s }).select().single();
  if (error) throw error;
  return data as SeccionGuion;
}

export async function guardarSeccionGuion(
  sb: SB, id: string, cambios: Partial<Pick<SeccionGuion, "titulo" | "contenido" | "canal" | "orden" | "activo">>,
): Promise<SeccionGuion> {
  const { data, error } = await sb.from("guion_secciones")
    .update({ ...cambios, actualizado_en: new Date().toISOString() }).eq("id", id).select().single();
  if (error) throw error;
  return data as SeccionGuion;
}

export async function leerConfigLlamadas(sb: SB, tenantId: string): Promise<ConfigLlamadas | null> {
  const { data, error } = await sb.from("config_llamadas").select("*").eq("tenant_id", tenantId).maybeSingle();
  if (error) throw error;
  return (data as ConfigLlamadas) ?? null;
}

export async function guardarConfigLlamadas(
  sb: SB, tenantId: string, c: Omit<ConfigLlamadas, "tenant_id">,
): Promise<ConfigLlamadas> {
  const { data, error } = await sb.from("config_llamadas")
    .upsert({ tenant_id: tenantId, ...c, actualizado_en: new Date().toISOString() }, { onConflict: "tenant_id" })
    .select().single();
  if (error) throw error;
  return data as ConfigLlamadas;
}

// ---------- T-CON-9: el conocimiento en fichas (R-CON-01 / R-CON-02) ---------
// ⚠️ Escriben DIRECTO a la tabla, igual que `perfil_bot` y por la misma razón:
// `conocimiento` se diseñó para que la editara el DUEÑO desde el panel (policy
// `conoc_wr … acceso_dueno`, migr `0004:172`, + grants en `0004:179`). No hay
// lógica que proteger detrás de una RPC — y la que sí hay (la baranda
// anti-precios) vive en un TRIGGER, que es donde no se la salta nadie: ni esta
// pantalla, ni quien escriba por la API con la sesión del dueño.

/** Todas las fichas del negocio, encendidas Y apagadas. */
export async function leerConocimiento(sb: SB, tenantId: string): Promise<FichaConocimiento[]> {
  const { data, error } = await sb
    .from("conocimiento").select("*").eq("tenant_id", tenantId).order("titulo");
  if (error) throw error;
  return (data ?? []) as FichaConocimiento[];
}

/**
 * ⛔⛔ EL CÓDIGO `P0004` ES LA BARANDA ANTI-PRECIOS DE LA BASE
 * (`chatai_app.guard_conocimiento`, `0004:86-97`), y esta función es lo ÚNICO
 * que la pantalla necesita saber de ella. **La baranda no se reimplementa
 * aquí**: si el navegador la copiara, tendríamos dos verdades y la del
 * navegador se la salta cualquiera escribiendo por la API.
 *
 * Se mira el `code` y, como respaldo, el marcador del texto: PostgREST devuelve
 * el `errcode` en `code`, pero un cliente distinto (o un `error` reenvuelto)
 * podría traer solo el mensaje. Sin respaldo, el dueño vería el texto crudo con
 * la palabra `permite_precios` dentro — un nombre de columna que no le dice
 * nada. Lo mide **V-35**.
 */
export function esRechazoPorPrecios(e: any): boolean {
  const code = e?.code ?? "";
  const msg = typeof e?.message === "string" ? e.message : "";
  return code === "P0004" || /permite_precios/.test(msg);
}

/**
 * ¿La base rechazó porque el título ya lo tiene otra ficha ENCENDIDA?
 *
 * Es el índice `conocimiento_titulo_unico_activo` de la `0049`. La pantalla ya
 * avisa ANTES de guardar (V-37), pero hay un camino que no pasa por ese aviso:
 * **encender** una ficha apagada cuyo título volvió a usarse mientras estaba
 * apagada. Ahí la base es la que corta, con un `23505` cuyo texto viene en
 * inglés y nombra el índice — que al dueño no le dice absolutamente nada.
 *
 * ⛔ Esto NO sustituye al índice ni al aviso: solo traduce el rechazo. La base
 * sigue siendo la única que lo impide siempre.
 */
export function esChoqueDeTitulo(e: any): boolean {
  const code = e?.code ?? "";
  const msg = typeof e?.message === "string" ? e.message : "";
  return code === "23505" || /conocimiento_titulo_unico_activo/.test(msg);
}

/**
 * Crea o actualiza UNA ficha y devuelve la fila que quedó en la base.
 *
 * ⛔⛔ `titulo` SOLO viaja cuando se está CREANDO. En una ficha que ya existe
 * este `update` no lo manda nunca, y no es una omisión: es la única forma de
 * que ningún camino de la pantalla pueda renombrar «garantia». El motor reparte
 * las fichas por su título EXACTO (`entrenamiento.ts:246`); un renombrado saca
 * la ficha de su campo y el bot deja de saber la garantía sin un solo error.
 * Lo mide **V-34**.
 */
export async function guardarFicha(
  sb: SB, tenantId: string,
  ficha: { id?: string; titulo: string; contenido: string; permite_precios: boolean },
  usuarioId: string | null,
): Promise<FichaConocimiento> {
  const comun = {
    contenido: ficha.contenido,
    permite_precios: ficha.permite_precios,
    actualizado_por: usuarioId,
  };
  if (ficha.id) {
    const { data, error } = await sb
      .from("conocimiento").update(comun)
      .eq("id", ficha.id).eq("tenant_id", tenantId)
      .select().single();
    if (error) throw error;
    return data as FichaConocimiento;
  }
  const { data, error } = await sb
    .from("conocimiento")
    .insert({ tenant_id: tenantId, categoria: "general", titulo: ficha.titulo, activo: true, ...comun })
    .select().single();
  if (error) throw error;
  return data as FichaConocimiento;
}

/**
 * Enciende o apaga una ficha. **APAGAR NO BORRA** (R-CON-01): el motor solo lee
 * `activo` (`entrenamiento_db.ts`), así que `activo = false` la saca del bot y
 * deja el texto intacto para volver a encenderla. Un `delete` aquí haría que
 * apagar «garantía» para revisarla se llevara el texto sin vuelta atrás.
 * Lo mide **V-33**.
 */
export async function cambiarActivoFicha(
  sb: SB, tenantId: string, id: string, activo: boolean, usuarioId: string | null,
): Promise<FichaConocimiento> {
  const { data, error } = await sb
    .from("conocimiento").update({ activo, actualizado_por: usuarioId })
    .eq("id", id).eq("tenant_id", tenantId)
    .select().single();
  if (error) throw error;
  return data as FichaConocimiento;
}

// ---------- C10: accesos (equipo del negocio) --------------------------------
// ⚠️ Invitar y revocar NO se llaman como RPC directa: van por la Edge Function
// `chatai-accesos`, que además manda el correo. La RPC sola crearía el vale y
// nadie se enteraría — y el token, que el panel no puede leer a propósito, se
// quedaría sin quien arme el enlace.

export interface MiembroEquipo {
  user_id: string; rol: "dueno" | "agente"; activo: boolean;
  email: string | null; nombre: string | null; desde: string;
}
export interface InvitacionEquipo {
  id: string; email: string; rol: "dueno" | "agente";
  vence_en: string; vencida: boolean; creada_en: string;
  /** Lo que pasó con el correo DESPUÉS de mandarlo (0033). `enviado` solo
   *  significa que Resend lo aceptó para intentarlo; el rebote llega luego. */
  correo_estado?: "enviado" | "entregado" | "rebotado" | "queja" | "demorado" | null;
  correo_detalle?: string | null;
}

/** Miembros + invitaciones vivas en UNA llamada, y sin el token. */
export async function cargarEquipo(
  sb: SB, tenantId: string,
): Promise<{ miembros: MiembroEquipo[]; invitaciones: InvitacionEquipo[] }> {
  const { data, error } = await sb.rpc("equipo_del_negocio", { p_tenant: tenantId });
  if (error) throw error;
  return {
    miembros: (data as any)?.miembros ?? [],
    invitaciones: (data as any)?.invitaciones ?? [],
  };
}

type RespuestaAccesos = { ok?: boolean; error?: string; aviso?: string; correo_enviado?: boolean };

async function accesos(sb: SB, body: Record<string, unknown>): Promise<RespuestaAccesos> {
  const { data, error } = await sb.functions.invoke("chatai-accesos", { body });
  // `functions.invoke` mete el cuerpo del error en `error.context`; sin esto el
  // asesor vería «Edge Function returned a non-2xx status code», que no dice nada.
  if (error) {
    const detalle = await (error as any)?.context?.json?.().catch(() => null);
    throw new Error(detalle?.error ?? "No se pudo completar la operación.");
  }
  if ((data as RespuestaAccesos)?.error) throw new Error((data as RespuestaAccesos).error);
  return data as RespuestaAccesos;
}

export function invitarMiembro(sb: SB, tenantId: string, email: string, rol: "dueno" | "agente") {
  return accesos(sb, { accion: "invitar", tenant_id: tenantId, email, rol });
}

/** Reenviar ROTA el token: el enlace anterior deja de servir en el acto. Es a
 *  propósito — un vale que anduvo dando vueltas por WhatsApp no debe seguir vivo. */
export function reenviarInvitacion(sb: SB, tenantId: string, email: string, rol: "dueno" | "agente") {
  return accesos(sb, { accion: "reenviar", tenant_id: tenantId, email, rol });
}

export function revocarInvitacion(sb: SB, id: string) {
  return accesos(sb, { accion: "revocar", id });
}

export async function cambiarRolMiembro(
  sb: SB, tenantId: string, userId: string, rol: "dueno" | "agente",
) {
  const { error } = await sb.rpc("cambiar_rol", { p_tenant: tenantId, p_user: userId, p_rol: rol });
  if (error) throw error;
}

/** `activo=false`, JAMÁS un borrado ni el «ban» de Supabase: esa cuenta es la
 *  MISMA de Victorix Work y bloquearla lo sacaría también de allá. */
export async function quitarAcceso(sb: SB, tenantId: string, userId: string) {
  const { error } = await sb.rpc("quitar_acceso", { p_tenant: tenantId, p_user: userId });
  if (error) throw error;
}

// ════════════════════════════════════════════════════════════════════════════
// C8 · ALERTAS DE OPERACIÓN — la franja del marco (T-ALE-14, R-ALE-01)
// ════════════════════════════════════════════════════════════════════════════

/** Cuánto atrás mira la franja PARA LOS AVISOS. Decisión de Cristhian
 *  (30-ago-2026): un aviso más viejo que esto no grita — con 5 incidentes de un
 *  problema ya resuelto a mano, una franja que los muestre para siempre se
 *  vuelve papel tapiz en una semana y deja de servir para lo único que se
 *  construyó: enterarse.
 *  ⭐ Y LA OTRA MITAD DE LA REGLA, QUE NO ES SIMÉTRICA: **un `grave` NO
 *  caduca.** Mientras siga abierto se ve, tenga los días que tenga. Medido
 *  antes de decidirlo: los dos graves abiertos de producción son del 24-ago y
 *  del 29-ago, o sea que con un corte plano la franja NACÍA VACÍA justo con lo
 *  que más importa. Callar un grave por viejo es el fallo que este tramo
 *  existe para evitar. */
export const VENTANA_AVISOS_HORAS = 48;

/** Cuántos incidentes abiertos caben. `v_alertas_abiertas` devuelve UNA fila
 *  por incidente, así que esto no es «las últimas 200 filas de la historia»:
 *  son 200 incidentes abiertos a la vez, que ya sería otra clase de problema.
 *  Y si alguna vez se alcanza, la franja LO DICE (ver `listarAlertasAbiertas`):
 *  un corte que tapa callado es justo lo que el fiscal cazó en la versión
 *  anterior, que leía `alertas` en crudo con `limit(500)`. */
export const TOPE_ALERTAS = 200;

/** ¿Este incidente todavía tiene que gritar? Un `grave` siempre; un `aviso`,
 *  solo dentro de la ventana. */
export function alertaVigente(a: Alerta, ahora = new Date()): boolean {
  if (a.severidad === "recuperado") return false;
  if (a.severidad === "grave") return true;               // ⭐ no caduca
  return new Date(a.creada_en).getTime() >= ahora.getTime() - VENTANA_AVISOS_HORAS * 3_600_000;
}

/**
 * De una lista de filas de `alertas`, los incidentes que la franja debe gritar.
 *
 * ⚠️ SEGUNDA RED, A PROPÓSITO. Quien decide de verdad es la vista
 * `v_alertas_abiertas` (migr 0043), que ya entrega una fila por clave y ya
 * aplica la regla — y lo hace con el reloj del SERVIDOR y sin que ningún corte
 * pueda tapar un grave. Esta función se conserva porque (a) es la única parte
 * comprobable sin levantar una base, y (b) si alguien mañana vuelve a leer
 * `alertas` en crudo desde aquí, la reducción sigue puesta y no se cuela un
 * incidente cerrado.
 *
 * `alertas` es append-only: una fila es un grito y `recuperado` cierra
 * (`0013:36-54`). Abierto = **la última fila de cada `clave`** cuya severidad
 * no sea `recuperado`.
 *
 * ⚠️ Se desempata por `id`, NO por `creada_en`: `id` es `generated always as
 * identity`, o sea el orden REAL de inserción. Dos filas de la misma clave
 * pueden compartir `creada_en` al milisegundo (misma transacción — el do-block
 * de un vigía o de un seed) y entonces ordenar por fecha deja el desempate al
 * azar del motor: la franja podría enseñar el `aviso` viejo TAPANDO el `grave`
 * nuevo, que es precisamente la severidad equivocada que este tramo persigue.
 *
 * ⛔ Esto NO es un filtro de seguridad. Lo que separa a un inquilino de otro es
 * la RLS (`alertas_sel`, `0013:371`), que corre en la base con la sesión del
 * usuario. Aquí solo se agrupa lo que la base YA decidió entregar.
 */
export function alertasAbiertas(filas: Alerta[], ahora = new Date()): Alerta[] {
  const ultima = new Map<string, Alerta>();
  for (const f of filas) {
    const previa = ultima.get(f.clave);
    if (!previa || f.id > previa.id) ultima.set(f.clave, f);
  }
  return [...ultima.values()]
    .filter((a) => alertaVigente(a, ahora))
    // Lo grave primero, y dentro de cada grupo lo más reciente arriba.
    .sort((a, b) => (a.severidad === b.severidad
      ? b.id - a.id
      : a.severidad === "grave" ? -1 : 1));
}

/**
 * Los incidentes abiertos que le conciernen a QUIEN ESTÁ MIRANDO.
 *
 * ⛔ Se consulta con la sesión del usuario y **filtra la RLS**: nada de
 * `service_role`, y nada de traerse las alertas de todos para filtrar en el
 * navegador — eso sería una fuga, no un filtro (R-ALE-01). Por eso aquí no hay
 * un solo `.eq("tenant_id", …)`: quien reparte es `alertas_sel` (`0013:371`),
 * que le da a Victorix todas y al dueño solo las de su inquilino. La vista es
 * `security_invoker`, así que esa política se evalúa igual a través de ella.
 *
 * ⛔ `detalle` —el motivo técnico— SOLO se pide si quien mira es Victorix. El
 * dueño lee una frase en su idioma («tu bot está atendiendo con la
 * configuración de hace 40 minutos»), no `permission denied for table
 * prompts_bot`. La vista entrega la fila entera; que el motivo técnico no le
 * llegue es decisión de la pantalla, y se escribe como tal: no viaja.
 *
 * ⚠️ Lanza si la consulta falla, y avisa (`incompleta`) si se llenó el tope. La
 * franja TIENE que decir las dos cosas: una franja vacía significa «todo bien»,
 * y callarse ante un error —o ante «no sé si los vi todos»— sería un botón que
 * miente.
 */
export async function listarAlertasAbiertas(
  sb: SB, opciones: { esVictorix: boolean; ahora?: Date },
): Promise<{ alertas: Alerta[]; incompleta: boolean }> {
  const columnas = "id, clave, severidad, tenant_id, titulo, creada_en"
    + (opciones.esVictorix ? ", detalle" : "");
  const { data, error } = await sb
    .from("v_alertas_abiertas").select(columnas)
    // ⭐ LOS GRAVES PRIMERO, y no es cosmética: si alguna vez se alcanzara el
    // tope, lo que se quedaría fuera serían `aviso`, nunca un `grave`. Sin este
    // orden, 200 avisos podían empujar fuera un grave abierto y la franja se
    // pintaba ÁMBAR habiendo un incidente rojo vivo — o sea, el color mintiendo,
    // que es el pecado que este carril ya cometió tres veces. Lo cazó un fiscal
    // (R2-5, 30-ago-2026). Alfabéticamente `grave` > `aviso`, y la vista ya
    // excluye `recuperado`, así que `ascending:false` los deja en ese orden.
    .order("severidad", { ascending: false })
    .order("id", { ascending: false })
    .limit(TOPE_ALERTAS);
  if (error) throw error;
  const filas = (data ?? []) as Alerta[];
  return {
    alertas: alertasAbiertas(filas, opciones.ahora),
    incompleta: filas.length >= TOPE_ALERTAS,
  };
}

/**
 * T-ALE-15 (R-ALE-02) · DAR UN INCIDENTE POR VISTO.
 *
 * ⭐ La ÚNICA escritura del panel sobre la operación, y va por RPC como manda la
 * cabecera de este archivo. No toca `chatai.alertas`: esa tabla es append-only y
 * es el registro de lo que pasó — no se le miente para apagar una luz. Lo que se
 * escribe es el satélite `alertas_vistas` (migr 0044), y solo puede hacerlo la
 * función, que lleva la comprobación de permiso escrita DENTRO (es
 * `security definer`, o sea que se salta la RLS).
 *
 * ⛔ Y NO ES UN «CERRAR». Si el incidente sigue ocurriendo, su siguiente grito
 * trae un `id` mayor que el archivado y vuelve a salir solo. El «ya lo vi» solo
 * puede archivar lo que ya no está pasando.
 *
 * Devuelve el `id` archivado, o `null` si no había nada que archivar (clave
 * desconocida o incidente ya `recuperado`). ⚠️ `null` NO es un error: es «ya no
 * está», y la pantalla lo trata igual que un éxito.
 * ⚠️ Un rebote SÍ es error y sube: `42501` cuando el incidente no es de este
 * negocio. Quien llama tiene que decirlo, no tragárselo.
 */
export async function darAlertaPorVista(
  sb: SB, clave: string, nota: string | null = null,
): Promise<number | null> {
  const { data, error } = await sb.rpc("marcar_alerta_vista", { p_clave: clave, p_nota: nota });
  if (error) throw error;
  return (data ?? null) as number | null;
}

// ---------- T-GUI-10: el guion por secciones (R-GUI-01 / R-GUI-03) ----------
// ⛔ SOLO LECTURA, Y ES DECISIÓN DEL TRAMO, no un olvido. Escribir el guion sin
// el versionado —que es T-GUI-11— sería pisar en producción el texto que
// gobierna al bot que hoy vende, sin nada a lo que volver. Aquí no hay ni un
// `insert` ni un `update`: la RLS `prompts_wr … soy_victorix()` (`0004:163`)
// sigue siendo la puerta, pero esta pantalla ni la toca.

/**
 * La versión ACTIVA del guion del inquilino, o `null` si no hay ninguna.
 *
 * ⚠️ `null` NO es un error y hay que tratarlo como caso normal: hoy **solo
 * Living House tiene fila** en `prompts_bot` (medido en producción el
 * 1-sep-2026). Cualquier otro inquilino que entre al panel cae aquí, y lo que
 * tiene que ver es una explicación en su idioma — no una pantalla en blanco ni
 * el crudo de Postgres. Lo mide **V-54**.
 */
export async function leerGuionActivo(sb: SB, tenantId: string): Promise<GuionActivo | null> {
  const { data, error } = await sb
    .from("prompts_bot")
    .select("id, version, activo, bloque_negocio, creado_por, creado_en")
    .eq("tenant_id", tenantId).eq("activo", true)
    .maybeSingle();
  if (error) throw error;
  return (data as GuionActivo | null) ?? null;
}

// ---------- T-GUI-11: versiones del guion y volver atrás (R-GUI-02) ---------
// ⛔⛔ LAS DOS ESCRITURAS VAN POR RPC, Y NO ES ESTILO. Cambiar de versión son
// dos escrituras —apagar la activa y encender la otra— y desde el navegador,
// por PostgREST, **son dos llamadas separadas sin transacción entre ellas**. Si
// la segunda no llega (se cae el wifi, se cierra la pestaña), el inquilino se
// queda SIN versión activa: el motor lanza `EntrenamientoIncompleto` y el bot
// se calla en mitad de una venta. Por eso aquí no hay ni un `.update()` sobre
// `prompts_bot`: las dos funciones de la migración `0051` lo hacen dentro de
// UNA transacción. Lo miden V-61, V-63 y el espía de escrituras de V-66.

/**
 * Todas las versiones del guion, la más nueva primero.
 *
 * ⚠️ Sin `bloque_negocio`: la lista solo necesita saber cuál está activa, quién
 * y cuándo. Traer el texto de todas sería mover el guion entero N veces por una
 * pantalla que solo pinta fechas.
 */
export async function leerVersionesGuion(sb: SB, tenantId: string): Promise<VersionGuion[]> {
  const { data, error } = await sb
    .from("prompts_bot")
    .select("id, version, activo, creado_por, creado_en")
    .eq("tenant_id", tenantId)
    .order("version", { ascending: false });
  if (error) throw error;
  return (data ?? []) as VersionGuion[];
}

/**
 * Guarda el texto como una versión NUEVA, ya activa, y apaga la anterior.
 *
 * ⛔ El número de versión NO viaja: lo calcula la base con la fila del
 * inquilino bloqueada. Mandarlo desde aquí es el mutante que mata V-65 — dos
 * pestañas guardando a la vez calcularían el mismo `max+1` y la segunda
 * reventaría con un `23505`.
 */
export async function guardarGuion(sb: SB, tenantId: string, texto: string): Promise<GuionActivo> {
  const { data, error } = await sb.rpc("guardar_guion", { p_tenant: tenantId, p_texto: texto });
  if (error) throw error;
  return data as GuionActivo;
}

/** Vuelve a una versión anterior: la enciende y apaga la activa, atómico. */
export async function activarVersionGuion(
  sb: SB, tenantId: string, version: number,
): Promise<GuionActivo> {
  const { data, error } = await sb.rpc("activar_version_guion",
    { p_tenant: tenantId, p_version: version });
  if (error) throw error;
  return data as GuionActivo;
}

// ---------- T-PRE-12: las reglas de precio (R-PRE-01) -----------------------
// ⚠️ ESCRIBEN DIRECTO A LA TABLA, igual que `perfil_bot` y por la misma razón:
// `reglas_precio` ya tiene su policy de escritura (`reglas_wr … soy_victorix()`,
// `0004:165`) y sus grants (`0004:178`). No hay dos escrituras que meter en una
// transacción —es UNA fila por inquilino— así que una RPC no compraría nada.
// La lógica que sí hay que proteger vive en un TRIGGER (`0053`), que es donde no
// se la salta nadie: ni esta pantalla, ni quien escriba por PostgREST.
//
// ⛔⛔ PERO OJO CON LO QUE LA BASE **NO** HACE AQUÍ. `soy_victorix()` es un flag
// GLOBAL del token (`0001:58-64`), no una membresía: la policy deja a Victorix
// escribir la fila de CUALQUIER negocio. **El único sitio donde se decide a qué
// inquilino se escribe es este archivo.** Un `update` sin el inquilino en el
// `upsert` no lo para la RLS: borraría las reglas de precio de todos los
// negocios a la vez, sin un solo error. Lo mide **V-106**, con vecino centinela.

export interface FilaReglasPrecio {
  tenant_id: string;
  config: ReglasPrecio;
  actualizado_en: string;
}

/** Las reglas de precio del inquilino, o `null` si todavía no tiene fila.
 *
 *  ⚠️ `null` NO es un error: hoy **solo Living House tiene fila** en
 *  `reglas_precio` (medido en producción el 1-sep-2026). Cualquier otro
 *  inquilino cae aquí y lo que tiene que ver es una explicación, no el crudo. */
export async function leerReglasPrecio(sb: SB, tenantId: string): Promise<FilaReglasPrecio | null> {
  const { data, error } = await sb
    .from("reglas_precio")
    .select("tenant_id, config, actualizado_en")
    .eq("tenant_id", tenantId)
    .maybeSingle();
  if (error) throw error;
  return (data as FilaReglasPrecio | null) ?? null;
}

/**
 * Guarda las reglas del inquilino — **y solo las suyas** (V-106).
 *
 * ⛔⛔ `upsert` con el `tenant_id` DENTRO del payload, y no `update`, por dos
 * razones distintas y las dos con víctima:
 *   · `update` no falla cuando no hay fila: **no hace nada**. Un negocio nuevo
 *     vería «guardado» y al recargar seguiría sin reglas — es exactamente el
 *     defecto que documenta `guardarPerfilBot`.
 *   · `update` sin `.eq("tenant_id", …)` toca TODAS las filas visibles, y con
 *     `soy_victorix()` son las de todos los negocios. El `onConflict` amarra la
 *     escritura a la clave primaria: no hay forma de que se salga de su fila.
 *
 * ⛔ `actualizado_en` NO viaja: lo pone el trigger de la `0053`. Mandarlo desde
 * el navegador dejaría al panel decidiendo cuándo se tocó una fila.
 */
export async function guardarReglasPrecio(
  sb: SB, tenantId: string, config: ReglasPrecio,
): Promise<FilaReglasPrecio> {
  const { data, error } = await sb
    .from("reglas_precio")
    .upsert({ tenant_id: tenantId, config }, { onConflict: "tenant_id" })
    .select("tenant_id, config, actualizado_en")
    .single();
  if (error) throw error;
  return data as FilaReglasPrecio;
}

/**
 * Pone en español lo que la base rechazó (V-105).
 *
 * ⚠️ Se lee el marcador `VX_REGLAS_INVALIDAS` del `detail`, no el texto del
 * mensaje: el texto cambia el día que alguien reescriba la migración, el
 * marcador no. Mismo patrón que `explicarFalloAlGuardar` en `Guion.tsx`.
 *
 * ⛔ Esto NO reimplementa la baranda: solo traduce lo que la base ya decidió. Y
 * jamás devuelve el crudo — un `VX_REGLAS_INVALIDAS:DESCUENTO` en la cara del
 * asesor no le dice qué arreglar, y el que se queda sin cotizar es el bot.
 */
export function explicarRechazoDeReglas(e: any): string {
  const detalle = `${e?.details ?? ""} ${e?.detail ?? ""} ${e?.hint ?? ""} ${e?.message ?? ""}`;
  if (detalle.includes("VX_REGLAS_INVALIDAS:SIN_MEDIOS")) {
    return "Deja al menos un medio de pago: sin ninguno el bot no puede dar precios y pasa cada " +
      "conversación a un asesor.";
  }
  if (detalle.includes("VX_REGLAS_INVALIDAS:DESCUENTO")) {
    return "Hay un descuento fuera de rango. Tiene que ser un número entre 0 y 99,99.";
  }
  // Ronda 2 · los dos marcadores que cierran H2 y H1 del fiscal.
  if (detalle.includes("VX_REGLAS_INVALIDAS:SIN_NOMBRE")) {
    return "Hay un medio de pago sin nombre. El bot le diría el precio a un cliente sin decirle de " +
      "qué forma de pago habla: ponle nombre o quítalo.";
  }
  if (detalle.includes("VX_REGLAS_INVALIDAS:REDONDEO")) {
    return "Ese redondeo no existe. Solo hay dos: «a miles» o «al peso».";
  }
  if (detalle.includes("VX_REGLAS_INVALIDAS:NOMBRE_REPETIDO")) {
    return "Hay dos medios de pago con el mismo nombre. El bot no sabría cuál aplicar: cámbiale el " +
      "nombre a uno o quítalo.";
  }
  if (detalle.includes("VX_REGLAS_INVALIDAS")) {
    return "La configuración de precios no es válida y la base la rechazó. Revisa los medios de " +
      "pago y sus descuentos.";
  }
  if (e?.code === "42501" || /row-level security|permission denied/i.test(String(e?.message ?? ""))) {
    return "Tu cuenta no puede cambiar las reglas de precio de este negocio. Solo Victorix las escribe.";
  }
  return "No se pudo guardar. Vuelve a intentarlo en un momento; las reglas siguen como estaban.";
}


// ---------- T-CAT-13: el estado del catálogo del bot (R-CAT-01) ------------

/**
 * El retrato del catálogo que el BOT tiene cargado, de `chatai.estado_catalogo`.
 *
 * ⛔⛔ AQUÍ NO SE CUENTA EL ERP, Y ÉSE ES TODO EL TRAMO. Un
 * `sb.schema("erp").from("v_catalogo_bot").select("*", { count: "exact" })`
 * daría un número más grande y más bonito, y sería falso de dos maneras:
 *   · mediría el ALMACÉN, no lo que el bot tiene cargado — con el worker caído
 *     seguiría diciendo «90 productos» mientras el bot no cotiza nada;
 *   · abriría una SEGUNDA PUERTA al catálogo. Esa vista **no** es
 *     `security_invoker` (dueño `postgres`): leerla se salta la RLS de las
 *     tablas base y cualquier autenticado vería el catálogo de cualquier
 *     inquilino. Y filtrarla por el `tenant_id` del panel no arregla nada: el
 *     de la vista es de OTRO mundo de identificadores, así que devolvería CERO
 *     productos con el bot atendiendo con noventa.
 *
 * ⚠️ El `.eq("tenant_id", …)` NO es decorativo aunque la RLS ya reparta: el
 * staff de Victorix pasa `soy_victorix()` y VE las filas de todos los negocios.
 * Sin el filtro, el panel le enseñaría el catálogo del vecino bajo el nombre
 * del negocio que tiene seleccionado. Lo mide V-155 con su inquilino centinela.
 */
export async function leerEstadoCatalogo(sb: SB, tenantId: string): Promise<EstadoCatalogo | null> {
  const { data, error } = await sb
    .from("estado_catalogo")
    .select("tenant_id, productos, variantes, fuente, generado_en, actualizado_en")
    .eq("tenant_id", tenantId)
    .maybeSingle();
  if (error) throw error;
  return (data as EstadoCatalogo | null) ?? null;
}

// ---------- T-CAL-5: los criterios de calificación editables (R-CRI-01/02) ----
// ⛔⛔ LA ESCRITURA VA POR RPC, NUNCA POR `.update()`: `authenticated` no tiene
// insert/update sobre `criterios_calificacion` ni sobre sus versiones (0069 · 0076),
// y guardar son DOS escrituras (la fila + su versión) que tienen que ir en UNA
// transacción. La puerta (dueño o Victorix), la lista cerrada de llaves y los rangos
// viven DENTRO de `chatai_app.guardar_criterios`: esta capa no los reimplementa.

/** La fila de criterios del negocio, o `null` si no tiene (⇒ «no está encendida»). */
export async function leerCriteriosCalificacion(sb: SB, tenantId: string): Promise<FilaCriterios | null> {
  const { data, error } = await sb
    .from("criterios_calificacion")
    .select("criterios, activa, actualizado_en")
    .eq("tenant_id", tenantId)
    .maybeSingle();
  if (error) throw error;
  return (data as FilaCriterios | null) ?? null;
}

/**
 * ⭐ T-CAL-11 · ¿la voz del bot está ENCENDIDA en este negocio? (`tenants.config.voz.activa`,
 * que escribe Victorix). Solo se pide `config->voz`, no el `config` entero (ahí viven el
 * chat de Telegram del negocio y otras cosas que esta pantalla no necesita). Encendida
 * SOLO con el booleano `true`, como la lee el worker (`voz_del_turno`, migr 0080).
 */
export async function leerVozDelNegocio(sb: SB, tenantId: string): Promise<boolean> {
  const { data, error } = await sb
    .from("tenants")
    .select("voz:config->voz")
    .eq("id", tenantId)
    .maybeSingle();
  if (error) throw error;
  return (data as { voz?: { activa?: unknown } } | null)?.voz?.activa === true;
}

/**
 * ⭐ T-CAL-21 · el gasto de la voz del mes (notas del bot, costo y por momento) contra el
 * presupuesto que puso Victorix. La puerta (dueño o Victorix, I-182) vive DENTRO de
 * `chatai_app.voz_del_mes` (0086): un agente recibe 42501. El panel ni lo pide para él.
 */
export async function leerVozDelMes(sb: SB, tenantId: string): Promise<VozDelMes> {
  const { data, error } = await sb.rpc("voz_del_mes", { p_tenant: tenantId });
  if (error) throw error;
  // ⚠️ Una respuesta con otra forma (base vieja sin la 0086, proxy raro) se trata como
  //    FALLO de lectura —«no se pudo leer»—, nunca como números: pintarla tumbaría la pestaña.
  const d = data as Partial<VozDelMes> | null;
  const n = (v: unknown) => typeof v === "number" && Number.isFinite(v);
  if (!d || typeof d !== "object" || !n(d.audios_mes) || !(d.max_mes === null || n(d.max_mes))
      || !(d.costo_mes_usd === null || n(d.costo_mes_usd))
      || !d.por_momento || !n(d.por_momento.recomendacion) || !n(d.por_momento.compra)) {
    throw new Error("voz_del_mes devolvió una forma inesperada");
  }
  return d as VozDelMes;
}

/**
 * ⭐ T-CAL-22 · I-184 · el cuadro «audio contra texto» de Métricas (`voz_resultados`, 0087).
 * Solo el dueño o Victorix (la base le dice 42501 a un agente: el panel ni se lo pide).
 * ⚠️ Una respuesta con otra forma (base sin la 0087, proxy raro) es FALLO de lectura, nunca
 *    números: pintarla tumbaría Métricas.
 */
export async function leerVozResultados(sb: SB, tenantId: string, semanas = 8): Promise<VozResultados> {
  const { data, error } = await sb.rpc("voz_resultados", { p_tenant: tenantId, p_semanas: semanas });
  if (error) throw error;
  const d = data as Partial<VozResultados> | null;
  const n = (v: unknown) => typeof v === "number" && Number.isFinite(v) && v >= 0;
  const filaBien = (f: any) => f && typeof f === "object"
    && (f.grupo_ab === "voz" || f.grupo_ab === "texto")
    && (f.momento === "recomendacion" || f.momento === "compra")
    && n(f.clientes) && n(f.respondieron_24h) && n(f.notas) && n(f.escuchadas)
    && (f.costo_usd === null || n(f.costo_usd));
  if (!d || typeof d !== "object" || typeof d.semana_en_curso !== "string" || !Array.isArray(d.semanas)
      || !d.semanas.every((s: any) => s && typeof s.semana === "string" && typeof s.incompleta === "boolean"
        && Array.isArray(s.filas) && s.filas.every(filaBien))) {
    throw new Error("voz_resultados devolvió una forma inesperada");
  }
  return d as VozResultados;
}

/** Las versiones de los criterios, la más nueva primero (sin el jsonb). */
export async function leerVersionesCriterios(sb: SB, tenantId: string): Promise<VersionCriterios[]> {
  const { data, error } = await sb
    .from("criterios_calificacion_versiones")
    .select("version, creado_por, creado_en, nota")
    .eq("tenant_id", tenantId)
    .order("version", { ascending: false });
  if (error) throw error;
  return (data ?? []) as VersionCriterios[];
}

/**
 * Guarda SOLO las llaves que cambiaron (misma forma anidada que `criterios`:
 * `{ puntos: { ciudad: 0 } }`). La base fusiona sobre la fila vigente y devuelve
 * el número de la versión nueva.
 */
export async function guardarCriterios(
  sb: SB, tenantId: string, editables: Record<string, unknown>, nota: string | null,
): Promise<number> {
  const { data, error } = await sb.rpc("guardar_criterios",
    { p_tenant: tenantId, p_editables: editables, p_nota: nota });
  if (error) throw error;
  return data as number;
}

/** Vuelve a los criterios de una versión: la base deja una versión nueva y la devuelve. */
export async function volverACriterios(sb: SB, tenantId: string, version: number): Promise<number> {
  const { data, error } = await sb.rpc("volver_a_criterios", { p_tenant: tenantId, p_version: version });
  if (error) throw error;
  return data as number;
}
